import React, { useCallback } from 'react';

import { runningInRNWebView, postMessageRN, WebToRNAction } from '@eika-infrastruktur/mobilbank-lib';
import { FileButton } from '@eika/button';

import { useConfig } from '../../../default-config';
import { DokumentDTO } from '../../../generated-types';
import { CONTEXT_PATH } from '../../../services';
import { useTranslation } from '../../../translations';
import { slugifyString } from '../../../utils/stringUtils';

import './SigningOrderAttachments.scss';

interface TaskDocumentsInterface {
    documents: DokumentDTO[];
}

const SigningOrderAttachments = ({ documents }: TaskDocumentsInterface) => {
    const { task } = useTranslation();

    const { featureToggles } = useConfig();

    const downloadUrl = (document: DokumentDTO) =>
        `${CONTEXT_PATH}/rest/resource/dokument/${document.id}/${encodeURIComponent(slugifyString(document.tittel))}?type=${document.type}`;

    const fileOnClick = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, documentDTO: DokumentDTO) => {
            if (runningInRNWebView) {
                if (documentDTO.type === 'pdf') {
                    e.preventDefault();
                    postMessageRN({
                        action: WebToRNAction.OPEN_PDF,
                        payload: {
                            url: downloadUrl(documentDTO),
                            title: documentDTO.tittel,
                        },
                    });
                } else if (featureToggles.enableOpenOtherDocumentsInAppBrowser) {
                    e.preventDefault();
                    postMessageRN({
                        action: WebToRNAction.OPEN_BROWSER,
                        payload: `${window.location.origin}${downloadUrl(documentDTO)}`,
                    });
                }
            }
        },
        [featureToggles],
    );

    if (!documents || documents.length === 0) return null;

    const documentsForSigning = documents.filter((document) => document.skalSignere);

    const attachments = documents.filter((document) => !document.skalSignere);

    return (
        <div className="signing-order-attachments">
            {documentsForSigning.length > 0 && (
                <>
                    <span className="typography-subtitle">{task.documentsTitle}</span>
                    <ul className="signing-order-attachments__attachments-list">
                        {documentsForSigning.map((documentForSigning) => (
                            <li key={documentForSigning.id}>
                                <FileButton
                                    key={documentForSigning.id}
                                    onClick={(e) => fileOnClick(e, documentForSigning)}
                                    href={downloadUrl(documentForSigning)}
                                    text={documentForSigning.tittel || ''}
                                    fileType={documentForSigning.type}
                                />
                            </li>
                        ))}
                    </ul>
                </>
            )}
            {attachments.length > 0 && (
                <>
                    <span className="typography-subtitle">{task.attachmentsTitle}</span>
                    <ul className="signing-order-attachments__attachments-list">
                        {attachments.map((attachment) => (
                            <li key={attachment.id}>
                                <FileButton
                                    key={attachment.id}
                                    onClick={(e) => fileOnClick(e, attachment)}
                                    href={downloadUrl(attachment)}
                                    text={attachment.tittel || ''}
                                    fileType={attachment.type}
                                />
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
};

export default SigningOrderAttachments;
